import React, { useState } from 'react';
import { useSystemNotifications } from '../hooks/useSystemNotifications';
import { Card, CardHeader, CardTitle, CardContent } from '../components/common/Card';
import { Button } from '../components/common/Button';
import { Badge } from '../components/common/Badge';
import { format } from 'date-fns';
import { BellIcon, CheckIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

const NotificationCenter: React.FC = () => {
  const { t } = useTranslation();
  const {
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    deleteAllRead,
    isMarkingAsRead,
    isMarkingAllAsRead,
    isDeletingNotification,
    isDeletingAllRead
  } = useSystemNotifications();

  const [filter, setFilter] = useState<'all' | 'unread'>('all');

  const filteredNotifications = notifications?.filter(notification => {
    if (filter === 'unread') return !notification.is_read;
    return true;
  }) || [];

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'leave_request_created':
      case 'leave_request_approved':
      case 'leave_request_rejected':
        return <BellIcon className="h-5 w-5" />;
      default:
        return <BellIcon className="h-5 w-5" />;
    }
  };

  const getNotificationColor = (type: string) => {
    switch (type) {
      case 'success':
      case 'leave_request_approved':
        return 'text-green-600 bg-green-100';
      case 'error':
      case 'leave_request_rejected':
        return 'text-red-600 bg-red-100';
      case 'warning':
        return 'text-yellow-600 bg-yellow-100';
      default:
        return 'text-blue-600 bg-blue-100';
    }
  };

  return (
    <div>
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('notifications.title')}</h1>
          <p className="mt-1 text-sm text-gray-500">
            {unreadCount ? t('notifications.unreadCount', { count: unreadCount }) : t('notifications.allRead', 'All notifications are read')}
          </p>
        </div>
        {filteredNotifications.length > 0 && (
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => markAllAsRead()}
              disabled={isMarkingAllAsRead || !unreadCount}
            >
              <CheckIcon className="h-4 w-4 mr-2" />
              {t('notifications.markAllAsRead', 'Oznacz wszystkie jako przeczytane')}
            </Button>
            <Button
              variant="outline"
              onClick={() => deleteAllRead()}
              disabled={isDeletingAllRead || !notifications?.some(n => n.is_read)}
              className="text-red-600 hover:text-red-700 border-red-300 hover:border-red-400"
            >
              <TrashIcon className="h-4 w-4 mr-2" />
              {t('notifications.deleteAllRead', 'Usuń przeczytane')}
            </Button>
          </div>
        )}
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>{t('notifications.allNotifications', 'All Notifications')}</CardTitle>
            <div className="flex gap-2">
              <Button
                variant={filter === 'all' ? 'primary' : 'outline'}
                size="sm"
                onClick={() => setFilter('all')}
              >
                {t('notifications.all', 'All')}
              </Button>
              <Button
                variant={filter === 'unread' ? 'primary' : 'outline'}
                size="sm"
                onClick={() => setFilter('unread')}
              >
                {t('notifications.unread', 'Unread')} ({unreadCount || 0})
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredNotifications.length === 0 ? (
            <div className="text-center py-8">
              <BellIcon className="mx-auto h-12 w-12 text-gray-400" />
              <p className="mt-2 text-sm text-gray-500">
                {filter === 'unread' ? t('notifications.noUnread', 'No unread notifications') : t('notifications.noNotifications')}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredNotifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`p-4 rounded-lg border ${
                    notification.is_read ? 'bg-gray-50 border-gray-200' : 'bg-white border-primary-200'
                  }`}
                >
                  <div className="flex items-start">
                    <div className={`p-2 rounded-full ${getNotificationColor(notification.type)}`}>
                      {getNotificationIcon(notification.type)}
                    </div>
                    <div className="ml-4 flex-1">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900">
                            {notification.title}
                          </p>
                          <p className="mt-1 text-sm text-gray-500">
                            {notification.message}
                          </p>
                          <p className="mt-2 text-xs text-gray-400">
                            {format(new Date(notification.created_at), 'MMM dd, yyyy at h:mm a')}
                          </p>
                        </div>
                        <div className="flex items-center gap-1 ml-4">
                          {!notification.is_read && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => markAsRead(notification.id)}
                              disabled={isMarkingAsRead}
                              title={t('notifications.markAsRead', 'Oznacz jako przeczytane')}
                            >
                              <CheckIcon className="h-4 w-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteNotification(notification.id)}
                            disabled={isDeletingNotification}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            title={t('notifications.delete', 'Usuń powiadomienie')}
                          >
                            <TrashIcon className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default NotificationCenter;